import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

// Upload timeout: 5 minutes for large files (CIMs can be 50+ MB)
export const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

const MB = 1024 * 1024;

/**
 * Per-extension size limits. MUST mirror SUPPORTED_UPLOADS in
 * backend/functions/src/documents/documentProcessor.ts — `initiateDocumentUpload` rejects any
 * extension or size not listed there, so allowing more here only turns a clear up-front message
 * into a failed document row.
 */
const MAX_UPLOAD_BYTES: Record<string, number> = {
  pdf: 50 * MB,
  docx: 50 * MB,
  xlsx: 50 * MB,
  xls: 50 * MB,
  xlsm: 50 * MB,
  csv: 25 * MB,
  txt: 10 * MB,
  md: 10 * MB,
  pptx: 50 * MB,
  png: 10 * MB,
  jpg: 10 * MB,
  jpeg: 10 * MB,
  gif: 10 * MB,
  webp: 10 * MB,
  zip: 50 * MB,
  msg: 25 * MB,
  eml: 25 * MB,
};

export const SUPPORTED_UPLOAD_EXTENSIONS: readonly string[] = Object.keys(MAX_UPLOAD_BYTES);

/** Comma-separated `accept` value for file inputs. */
export const UPLOAD_ACCEPT_ATTRIBUTE = SUPPORTED_UPLOAD_EXTENSIONS.map(ext => `.${ext}`).join(',');

function fileExtension(fileName: string): string {
  const base = fileName.split(/[\\/]/).pop()?.trim() ?? '';
  const lastDot = base.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === base.length - 1) return '';
  return base.slice(lastDot + 1).toLowerCase();
}

/**
 * OS and Office housekeeping files that folder drops sweep in (.DS_Store, Thumbs.db, desktop.ini,
 * `~$Model.xlsx` lock stubs). They are never documents, and a lock stub has a valid extension but
 * no valid content, so it would upload and then fail extraction.
 */
export function isIgnorableUploadFile(fileName: string): boolean {
  const base = fileName.split(/[\\/]/).pop()?.trim().toLowerCase() ?? '';
  return base === '.ds_store' || base === 'thumbs.db' || base === 'desktop.ini' || base.startsWith('~$');
}

/** Returns a user-facing reason the file cannot be uploaded, or null when it can. */
export function validateUploadFile(file: { name: string; size: number }): string | null {
  const extension = fileExtension(file.name);
  const maxBytes = MAX_UPLOAD_BYTES[extension];
  if (maxBytes === undefined) return `${file.name}: File type not supported`;
  if (file.size <= 0) return `${file.name}: File is empty`;
  if (file.size > maxBytes) {
    return `${file.name}: File size must be less than ${Math.round(maxBytes / MB)}MB for .${extension} files`;
  }
  return null;
}

/**
 * Upload one file to a project's Knowledge Base and return the new document id.
 *
 * Plain async function (no hooks) so any surface can use it — e.g. a camera capture:
 * `uploadProjectDocument(projectId, new File([blob], 'photo.jpg', { type: 'image/jpeg' }))`.
 * The document row is created by the server, the bytes go straight to Cloud Storage through a
 * signed URL, and the server pipeline (extraction -> indexing) takes over on finalize. If any step
 * after the row exists fails, the row is marked failed exactly once so it never lingers as
 * 'pending'. Throws an Error with a user-presentable message.
 */
export async function uploadProjectDocument(projectId: string, file: File): Promise<string> {
  let documentId: string | undefined;
  let markedFailed = false;

  const markFailed = async (errorMessage: string): Promise<void> => {
    if (!documentId || markedFailed) return;
    markedFailed = true;
    try {
      const markUploadFailed = httpsCallable(functions, 'markUploadFailed');
      await markUploadFailed({ documentId, errorMessage });
    } catch (markError) {
      console.error('Failed to mark document as failed:', markError);
    }
  };

  try {
    const initiateUpload = httpsCallable<
      { projectId: string; fileName: string; fileType: string; fileSize: number },
      { documentId?: string; uploadUrl?: string; contentType?: string }
    >(functions, 'initiateDocumentUpload');
    const result = await initiateUpload({
      projectId,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    });

    const { uploadUrl, contentType } = result.data;
    documentId = result.data.documentId;
    if (!documentId || !uploadUrl) {
      throw new Error('Failed to initialize upload - missing documentId or uploadUrl');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
    let uploadResponse: Response;
    try {
      uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType || file.type || 'application/octet-stream' },
        body: file,
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed with status ${uploadResponse.status}: ${uploadResponse.statusText}`);
    }
    return documentId;
  } catch (err: unknown) {
    console.error('Error uploading document:', err);
    const error = err as { name?: string; message?: string };
    const message = error?.name === 'AbortError'
      ? 'Upload timed out. Please check your connection and try again.'
      : error?.message || 'Failed to upload document';
    await markFailed(message);
    throw new Error(message);
  }
}
